import React, { useState } from "react";
import { BASE_URL } from "../../../Configuration/baseUrls";
import stylez from "./Salestable.module.css";
import SalesOrderModal from "../../../Components/SalesOrder/SalesOrderModal/SalesOrderModal";
import ContactsModal from "../../../Components/SalesOrder/ContactsModal/ContactModal";
import { FaTrash, FaFilePdf, FaDownload } from "react-icons/fa";

const SalesTable = (props) => {
  const [show, setShow] = useState(false);
  const [products, setProducts] = useState();
  const [showContacts, setShowContacts] = useState(false);
  const [contactId, setContactId] = useState();
  const [generatingPDF, setGeneratingPDF] = useState(null);
  const [selectedOrders, setSelectedOrders] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);

  const handleModal = (i) => {
    setShow(true);
    setProducts(i);
  };

  const handleContacts = (i) => {
    setContactId(i);
    setShowContacts(true);
  };

  // Checkbox selection handlers
  const handleSelectOrder = (orderId) => {
    setSelectedOrders(prev => {
      const newSelected = new Set(prev);
      if (newSelected.has(orderId)) {
        newSelected.delete(orderId);
      } else {
        newSelected.add(orderId);
      }
      return newSelected;
    });
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedOrders(new Set());
    } else {
      const allOrderIds = new Set(orders.map(order => order._id));
      setSelectedOrders(allOrderIds);
    }
    setSelectAll(!selectAll);
  };

  const {
    orders,
    updateOrder,
    deleteOrder,
    page,
    pageSize,
    handleNext,
    handlePrevious,
    count,
    tableHeaders,
    showPaymentColumns,
    showDriverCommentColumns,
    onBulkPDFGenerate,
  } = props;

  // Update selectAll state when individual selections change
  React.useEffect(() => {
    if (orders) {
      const allSelected = orders.every(order => selectedOrders.has(order._id));
      const noneSelected = selectedOrders.size === 0;
      setSelectAll(allSelected && !noneSelected);
    }
  }, [selectedOrders, orders]);

  const handleDownload = (imageUrl) => {
    console.log(imageUrl);
    const link = document.createElement("a");
    link.target = "_blank";
    link.href = imageUrl;
    link.download = imageUrl.substring(imageUrl.lastIndexOf("/") + 1);
    link.click();
  };

  // ✅ UPDATED: More robust PDF generation with detailed error handling
  const handleGeneratePDF = async (order) => {
    console.log('🔍 Starting PDF generation process...');
    console.log('📋 Order data:', order);
    
    try {
      setGeneratingPDF(order._id);
      
      // ✅ Step 1: Validate order data
      if (!order) {
        throw new Error('Order data is missing or undefined');
      }
      
      if (!order.orderNumber) {
        throw new Error('Order number is missing');
      }
      
      console.log('✅ Order validation passed');
      
      // ✅ Step 2: Test if PDF libraries are available
      console.log('🔍 Testing PDF library availability...');
      
      let jsPDF, autoTable;
      try {
        // Try to import jsPDF
        const jsPDFModule = await import('jspdf');
        jsPDF = jsPDFModule.default;
        console.log('✅ jsPDF imported successfully');
        
        // Try to import autoTable
        await import('jspdf-autotable');
        console.log('✅ jspdf-autotable imported successfully');
        
      } catch (importError) {
        console.error('❌ Error importing PDF libraries:', importError);
        throw new Error(`PDF libraries not available: ${importError.message}. Please install: npm install jspdf jspdf-autotable`);
      }
      
      // ✅ Step 3: Test basic PDF creation
      console.log('🔍 Testing basic PDF creation...');
      try {
        const testDoc = new jsPDF();
        testDoc.text('Test', 10, 10);
        console.log('✅ Basic PDF creation works');
      } catch (pdfError) {
        console.error('❌ Error creating basic PDF:', pdfError);
        throw new Error(`PDF creation failed: ${pdfError.message}`);
      }
      
      // ✅ Step 4: Try to import our PDF generator
      console.log('🔍 Importing PDF generator...');
      let generateOrderPDF;
      try {
        const pdfModule = await import('../../../utils/pdfGenerator');
        generateOrderPDF = pdfModule.generateOrderPDF;
        console.log('✅ PDF generator imported successfully');
      } catch (generatorError) {
        console.error('❌ Error importing PDF generator:', generatorError);
        
        // ✅ Final fallback: Very simple PDF
        const doc = new jsPDF();
        
        doc.setFontSize(20);
        doc.text('FIREBRAND BBQ', 20, 30);
        doc.setFontSize(14);
        doc.text(`Order #${order.orderNumber}`, 20, 50);
        doc.text(`Customer: ${order.customer?.businessName || 'N/A'}`, 20, 70);
        doc.text(`Date: ${order.date ? new Date(order.date).toLocaleDateString('en-GB') : 'N/A'}`, 20, 90);
        doc.text(`Type: ${order.isPickup ? 'Pickup' : 'Delivery'}`, 20, 110);
        
        doc.text('Products:', 20, 130);
        let yPos = 150;
        (order.products || []).forEach((item, index) => {
          const productName = item.product?.productName || 'N/A';
          const quantity = item.quantity || 0;
          doc.text(`${index + 1}. ${productName} - Qty: ${quantity}`, 20, yPos);
          yPos += 15;
        });
        
        const filename = `Order_${order.orderNumber}_Simple.pdf`;
        doc.save(filename);
        
        console.log('✅ Simple fallback PDF generated successfully');
        alert('PDF generated successfully using simple method!');
        return;
      }
      
      // ✅ Step 5: Generate PDF using our generator
      console.log('🔍 Generating PDF using full generator...');
      
      // Add a small delay for UX
      await new Promise(resolve => setTimeout(resolve, 500));
      
      generateOrderPDF(order);
      
      console.log(`✅ PDF generated successfully for order ${order.orderNumber}`);
      alert(`PDF generated successfully for Order #${order.orderNumber}!`);
      
    } catch (error) {
      console.error('❌ Complete error details:', {
        message: error.message,
        stack: error.stack,
        name: error.name,
        order: order
      });
      
      // ✅ User-friendly error message based on error type
      let userMessage = 'Failed to generate PDF. ';
      
      if (error.message.includes('PDF libraries not available')) {
        userMessage = 'PDF generation requires additional libraries. Please contact support or try refreshing the page.';
      } else if (error.message.includes('Order data is missing')) {
        userMessage = 'Order data is incomplete or corrupted.';
      } else if (error.message.includes('Order number is missing')) {
        userMessage = 'This order is missing required information (order number).';
      } else if (error.message.includes('PDF creation failed')) {
        userMessage = 'PDF creation failed. This might be a browser compatibility issue.';
      } else {
        userMessage += `Technical details: ${error.message}`;
      }
      
      alert(userMessage);
      
      // ✅ Also log to console for debugging
      console.error('❌ PDF Generation Failed:', {
        errorMessage: error.message,
        orderNumber: order?.orderNumber,
        hasCustomer: !!order?.customer,
        hasProducts: !!(order?.products && order.products.length > 0),
        browserInfo: navigator.userAgent
      });
      
    } finally {
      setGeneratingPDF(null);
    }
  };

  return (
    <>
      <div className={stylez.tableContainer}>
        {show && (
          <SalesOrderModal show={show} setShow={setShow} products={products} />
        )}
        {showContacts && (
          <ContactsModal
            show={showContacts}
            setShow={setShowContacts}
            id={contactId}
          />
        )}
        
        {/* ✅ Bulk Actions */}
        {orders && orders.length > 0 && (
          <div className={stylez.bulkActionsContainer}>
            <div className={stylez.bulkActions}>
              <span className={stylez.bulkLabel}>Bulk Actions:</span>
              <button
                className={stylez.bulkPDFBtn}
                onClick={() => {
                  const selectedOrdersArray = orders.filter(order => selectedOrders.has(order._id));
                  if (selectedOrdersArray.length > 0) {
                    onBulkPDFGenerate && onBulkPDFGenerate(selectedOrdersArray);
                  } else {
                    alert('Please select orders to generate PDF');
                  }
                }}
                disabled={selectedOrders.size === 0}
                title={selectedOrders.size > 0 ? `Generate PDF for ${selectedOrders.size} selected orders` : "Select orders to generate PDF"}
              >
                <FaFilePdf /> Generate PDF ({selectedOrders.size} selected)
              </button>
            </div>
          </div>
        )}

        <table className={stylez.tableStyle}>
          <thead className={stylez.tableHeader}>
            <tr>
              <th>
                <input
                  type="checkbox"
                  checked={selectAll}
                  onChange={handleSelectAll}
                  title="Select all orders"
                />
              </th>
              {tableHeaders?.map((i, index) => (
                <th key={index}>{i}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {orders?.map((i, index) => (
              <tr key={index}>
                <td>
                  <input
                    type="checkbox"
                    checked={selectedOrders.has(i._id)}
                    onChange={() => handleSelectOrder(i._id)}
                    title="Select this order"
                  />
                </td>
                <td>{i?.orderNumber}</td>
                <td
                  className={stylez.contactField}
                  onClick={() => handleContacts(i?.customer?._id)}
                >
                  {i?.customer?.businessName}
                  {i?.scheduled ? <span>🔄</span> : null}
                  {!i?.date && <span title="Date not set" style={{color: '#dc3545'}}>📅❗</span>}
                </td>
                <td>{i && i?.purchaseOrdeNo ? i?.purchaseOrdeNo : "N/A"}</td>
                <td>{i && i?.comments ? i?.comments : "N/A"}</td>

                <td>
                  <button
                    className="popupBtn"
                    onClick={() => handleModal(i?.products)}
                  >
                    {i?.products?.length}{" "}
                  </button>
                </td>
                <td>{new Date(i?.date).toLocaleDateString("en-GB")}</td>

                {tableHeaders.includes("Order Type") && (
                  <td>
                    <span
                      className={`${stylez.orderTypeBadge} ${
                        i?.isPickup ? stylez.pickup : stylez.delivery
                      }`}
                    >
                      {i?.isPickup ? "Pickup" : "Delivery"}
                    </span>
                  </td>
                )}

                {showPaymentColumns && (
                  <>
                    <td>{i?.paymentType || "N/A"}</td>
                    <td>{i?.paymentCollected ? "Yes" : "No"}</td>
                  </>
                )}

                {tableHeaders.includes("Order Status") ? (
                  <td>
                    <span
                      className={`${stylez.statusBadge} ${stylez[i?.status?.toLowerCase()]}`}
                    >
                      {i?.status === "Assigned"
                        ? `Assigned to ${i?.run}`
                        : i?.status}
                    </span>
                  </td>
                ) : null}

                {i?.photos.length <= 0 ? (
                  <td>N/A</td>
                ) : (
                  <td className={stylez.imageContainer}>
                    {i?.photos?.map((i, index) => {
                      let imageUrl = i ? i : null;
                      return (
                        <div key={index}>
                          <img
                            crossOrigin="anonymous"
                            alt="deliveryimages"
                            src={`${imageUrl}`}
                            width={"40px"}
                            style={{ objectFit: "cover" }}
                            onClick={() => handleDownload(imageUrl)}
                          />
                        </div>
                      );
                    })}
                  </td>
                )}

                {showDriverCommentColumns && (
                  <td className={stylez.driverCommentsCell}>
                    {i && i?.driverComments ? (
                      <div className={stylez.commentsWrapper}>
                        <span className={stylez.commentsText}>
                          {i.driverComments.length > 50
                            ? `${i.driverComments.substring(0, 50)}...`
                            : i.driverComments}
                        </span>
                        {i.driverComments.length > 50 && (
                          <button
                            className={stylez.expandBtn}
                            title={i.driverComments}
                          >
                            📄
                          </button>
                        )}
                      </div>
                    ) : (
                      <span className={stylez.noComments}>N/A</span>
                    )}
                  </td>
                )}

                <td className={stylez.updateField}>
                  <div className={stylez.actionButtons}>
                    <button className="editBtn" onClick={() => updateOrder(i)}>
                      Update Order
                    </button>
                    
                    {/* ✅ PDF Generation Button */}
                    <button
                      className={stylez.pdfBtn}
                      onClick={() => handleGeneratePDF(i)}
                      disabled={generatingPDF === i._id}
                      title="Generate PDF for this order"
                    >
                      {generatingPDF === i._id ? (
                        <span className={stylez.spinner}>⏳</span>
                      ) : (
                        <FaFilePdf />
                      )}
                      {generatingPDF === i._id ? 'Generating...' : 'PDF'}
                    </button>
                    
                    <button
                      className="deleteBtn"
                      onClick={() => {
                        window.confirm(
                          "Are you sure you want to delete this Order?"
                        ) && deleteOrder(i._id);
                      }}
                    >
                      <FaTrash />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className={stylez.pagination}>
        <button
          disabled={page === 1}
          onClick={handlePrevious}
          className={stylez.pageButton}
        >
          Previous
        </button>
        <span className={stylez.pages}>
          Page -<span className={stylez.pageNumber}>{page} </span>
        </span>
        <button
          disabled={count < pageSize}
          onClick={handleNext}
          className={stylez.pageButton}
        >
          Next
        </button>
      </div>
    </>
  );
};

export default SalesTable;
